import type { PriceImportRun, PriceImportRunStatus } from 'apis/price-imports';

export type RunStep = 'mapping' | 'rows' | 'approvals' | 'commit';

export interface RunStatusPresentation {
  label: string;
  colorScheme: string;
}

export const RUN_STATUS_PRESENTATION: Record<PriceImportRunStatus, RunStatusPresentation> = {
  uploaded: { label: 'Uploaded', colorScheme: 'gray' },
  adapting: { label: 'Reading', colorScheme: 'blue' },
  adapt_failed: { label: 'Needs mapping', colorScheme: 'orange' },
  matching: { label: 'Matching', colorScheme: 'blue' },
  review: { label: 'In review', colorScheme: 'orange' },
  committing: { label: 'Committing', colorScheme: 'blue' },
  committed: { label: 'Committed', colorScheme: 'green' },
  partially_committed: { label: 'Partly committed', colorScheme: 'yellow' },
  failed: { label: 'Failed', colorScheme: 'red' },
  cancelled: { label: 'Cancelled', colorScheme: 'gray' },
};

export function runStatusPresentation(status: PriceImportRunStatus): RunStatusPresentation {
  return RUN_STATUS_PRESENTATION[status] ?? { label: status, colorScheme: 'gray' };
}

/**
 * Where to land an operator returning to a run.
 *
 * The run is durable and resumable, so coming back should drop them where the
 * work actually is rather than always at step one.
 */
export function defaultStepFor(status: PriceImportRunStatus): RunStep {
  switch (status) {
    case 'uploaded':
    case 'adapting':
    case 'adapt_failed':
      return 'mapping';
    case 'committed':
    case 'partially_committed':
    case 'committing':
      return 'commit';
    default:
      return 'rows';
  }
}

/** A committed run is history: the whole screen goes read-only. */
export function isRunReadOnly(status: PriceImportRunStatus): boolean {
  return status === 'committed' || status === 'committing' || status === 'cancelled';
}

/**
 * Whether Commit may be pressed, and why not when it may not.
 *
 * Rows still needing a decision do NOT block — they are skipped — but the UI
 * has to say so out loud, because silently dropping 71 of a supplier's lines is
 * exactly the kind of thing an operator discovers a week later.
 */
export function commitPreflight(
  run: Pick<PriceImportRun, 'status' | 'matchedRows' | 'reviewRows' | 'proposalRows' | 'errorRows'>
): {
  canCommit: boolean;
  blockingReason?: string;
  warnings: string[];
} {
  const warnings: string[] = [];

  if (isRunReadOnly(run.status)) {
    return { canCommit: false, blockingReason: 'This import has already been committed.', warnings };
  }

  if (run.status !== 'review' && run.status !== 'partially_committed') {
    return { canCommit: false, blockingReason: 'This import is not ready to commit yet.', warnings };
  }

  if (run.matchedRows === 0 && run.proposalRows === 0) {
    return { canCommit: false, blockingReason: 'No rows are ready to commit.', warnings };
  }

  if (run.reviewRows > 0) {
    warnings.push(`${run.reviewRows} rows still need a decision and will be skipped.`);
  }

  if (run.errorRows > 0) {
    warnings.push(`${run.errorRows} rows have errors and will be skipped.`);
  }

  return { canCommit: true, warnings };
}
